import { useFetch } from "@hyper-fetch/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

import { sdk } from "@/api/sdk";
import { consumeRedirectPath, resolveRedirect } from "@/features/auth/utils/post-auth-redirect";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/(public)/callback/")({
  staticData: { sitemap: false },
  component: RouteComponent,
  loader: () => ({ crumb: m.auth_callback_crumb() }),
});

/**
 * Landing spot for OAuth sign-ins. The provider round-trip can't run the
 * client-side redirect logic the email/password form uses, so both flows come
 * back here and this route decides the final destination: the stored redirect, or the default.
 */
function RouteComponent() {
  const navigate = useNavigate();
  const { data, loading, error } = useFetch(sdk.users.me.$get);

  useEffect(() => {
    if (loading) return;

    // No session (or the /users/me call failed) means auth didn't take, so send
    // them back to sign in.
    if (error || !data) {
      navigate({ to: "/login", replace: true });
      return;
    }

    const target = resolveRedirect(consumeRedirectPath());
    if ("external" in target) {
      window.location.replace(target.external);
      return;
    }
    navigate({ href: target.internal, replace: true });
  }, [data, loading, error, navigate]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 text-center">
      <Loader2 className="text-muted-foreground size-6 animate-spin" />
      <p className="text-muted-foreground text-sm">{m.auth_callback_message()}</p>
    </div>
  );
}
